import { Box } from '@repo/ui/Box';
import { IconButton } from '@repo/ui/IconButton';
import { Popover } from '@repo/ui/Popover';
import { PopoverContentSimple } from '@repo/ui/Popover/PopoverContentSimple';
import { PopoverRoot } from '@repo/ui/Popover/PopoverRoot';
import { PopoverTrigger } from '@repo/ui/Popover/PopoverTrigger';
import { Separator } from '@repo/ui/Separator';
import { Flex } from '@repo/ui/Flex';

import type { OrderItem } from '../../../graphql/query/getOrder';
import s from './ItemSelectionTask.module.css';
import {
  ineligibleHeader,
  itemSelectionStepId,
} from '../../../layouts/ItemSelection/baseConfig';
import { rawString, Text } from '../../Language/Language';
import { ItemCardButtonView } from '../../ItemCard/ItemCardView';
import { ShowMoreButton, showMoreCutoff } from './ShowMoreButton';

export interface IneligibleItemsViewProps {
  ineligibleItems: OrderItem[];
  isMobile: boolean;
  showMore: boolean;
  onShowMore: () => void;
  onItemClick: (itemId: string) => void;
  orderNumber?: string;
  customerFirstName?: string;
  customerLastName?: string;
}

/**
 * Pure presentational component for displaying ineligible items.
 * Contains no business logic, hooks, or side effects.
 * All data and callbacks are provided via props.
 */
export const IneligibleItemsView = ({
  ineligibleItems,
  isMobile,
  showMore,
  onShowMore,
  onItemClick,
  orderNumber,
  customerFirstName,
  customerLastName,
}: IneligibleItemsViewProps) => {
  if (!ineligibleItems || ineligibleItems.length === 0) {
    return null;
  }

  return (
    <>
      <Box padding={['md:py-64', 'py-32']}>
        <Separator size="lg" />
      </Box>
      <Box padding={['px-24', 'md:px-0']}>
        <Flex alignItems="center" className={s.headerContainer} gap="g-8">
          <h1 className={s[`${itemSelectionStepId}-${ineligibleHeader}`]}>
            <Text
              langKey={`Global.${itemSelectionStepId}.IneligibleHeader`}
              replacementTokens={{
                orderNumber: orderNumber ?? '',
                firstName: customerFirstName ?? '',
                lastName: customerLastName ?? '',
              }}
            />
          </h1>
          <PopoverRoot>
            <PopoverTrigger asChild={true}>
              <IconButton
                icon="help-circle"
                label={rawString({
                  langKey: 'Global.help',
                })}
              />
            </PopoverTrigger>
            <Popover
              align={isMobile ? 'end' : 'center'}
              ariaLabel={rawString({
                langKey: 'Global.info',
              })}
              hasArrow={false}
            >
              <PopoverContentSimple
                closeButtonLabel={rawString({
                  langKey: 'Global.close',
                })}
              >
                <Text langKey="Global.itemSelection.IneligibleHeaderHelp" />
              </PopoverContentSimple>
            </Popover>
          </PopoverRoot>
        </Flex>
        <Flex direction="column" gap="g-16">
          {ineligibleItems.map((item, i) => {
            if (!showMore && i > showMoreCutoff - 1) return null;
            return (
              <ItemCardButtonView
                key={item.id}
                aria-labelledby={`${item.id}-title`}
                imageSize={isMobile ? 'sm' : 'xl'}
                item={item}
                onClick={() => onItemClick(item.id)}
                disabled={true}
                remainingQuantity={item.quantity?.remaining || 1}
                isShipmentScopeReason={false}
              />
            );
          })}
        </Flex>
        <ShowMoreButton
          onClick={onShowMore}
          showMore={showMore}
          hiddenItemCount={ineligibleItems.length - showMoreCutoff}
        />
      </Box>
    </>
  );
};
